import { ArrayHelper } from "@/utils/helpers/array-helper";
import { FileHelper } from "@/utils/helpers/file-helper";
import { SignatureScreenSectionProps } from "@/views/core/extranet/transactions/signature/SignatureScreen";
import { columns } from "@/views/core/extranet/transactions/signature/columns/FilesColumns";
import SubmitButton from "@components/core/buttons/SubmitButton";
import ImportFilesCard from "@components/core/cards/ImportFilesCard";
import ConfirmModal from "@components/core/modals/ConfirmModal";
import InfoModal from "@components/core/modals/InfoModal";
import Confidentiality from "@components/core/subComponents/Confidentiality";
import GenericSortableTable from "@components/core/tables/GenericSortableTable";
import { authenticationStore } from "@state/auth/AuthStore";
import { requestSearchContactCategoriesForOffice } from "@state/contact/ContactsEffects";
import { setContactField } from "@state/contact/ContactsEvents";
import { requestSearchMembersForOffice } from "@state/offices/OfficesEffects";
import {
  removeTransactionFile,
  setCurrentTransactionField,
  setTransactionFiles,
} from "@state/transactions/CurrentTransactionEvents";
import { useCurrentTransaction } from "@state/transactions/CurrentTransactionHook";
import { AuthenticationContext } from "@type/core/auth/auth.types";
import { Contact, File } from "@type/core/dashboard/dashboard.types";
import {
  MAX_SIZE_ALL_FILES,
  MAX_SIZE_PER_FILE,
  SCELLEMENT_MAX_FILES,
  SIGNATURE_MAX_FILES,
  formValidateTriggers,
} from "@utils/Constant";
import { toastError } from "@utils/helpers/toast-helper";
import { Checkbox, Col, Form, Row } from "antd";
import { useUnit } from "effector-react";
import {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

interface FilesToSignSectionProps extends SignatureScreenSectionProps {
  isSignature: boolean;
  description: string;
  descriptionSeparator: string;
  descriptionUrlTitle: string;
  isFromModel?: boolean;
  signaturePositioning?: boolean;
  setSignaturePositioning?: Dispatch<SetStateAction<boolean>>;
  className?: string;
  currentFileCount: number;
}

const FilesToSignSection: FunctionComponent<FilesToSignSectionProps> = ({
  onSubmit,
  isSignature,
  description,
  descriptionSeparator,
  descriptionUrlTitle,
  isFromModel = false,
  signaturePositioning,
  setSignaturePositioning,
  className = "",
  currentFileCount,
}) => {
  const { t } = useTranslation();

  const { transactionForm, files } = useCurrentTransaction();

  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);
  const user = authStore?.user;

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [members, setMembers] = useState<Contact[]>([]);

  const handleSubmit = () => {
    onSubmit?.();
  };

  useEffect(() => {
    if (transactionForm.office) {
      void requestSearchContactCategoriesForOffice({
        id: transactionForm.office,
      });
    }
  }, [transactionForm.office]);

  useEffect(() => {
    return requestSearchContactCategoriesForOffice.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setContactField([{ field: "category", value: result.data.categories }]);
      } else {
        toastError(t("error.retrieveCategories"));
      }
    });
  });

  useEffect(() => {
    if (transactionForm.office) {
      void requestSearchMembersForOffice({ id: transactionForm.office });
    }
  }, [transactionForm.office]);

  useEffect(() => {
    return requestSearchMembersForOffice.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setMembers(result.data.contacts || []);
      } else {
        toastError(t("error.retrieveMembers"));
      }
    });
  });

  return (
    <Row className={`justify-content-center ${className}`}>
      <Col className="text-primary text-center mb-8">
        {t("home.newRequest.pdfsCompatibilityWarning")}
        <InfoModal value="pdfsCompatibility" info />
      </Col>
      <Col span={24} lg={20} xl={16}>
        <Form
          data-test={"confidentiality"}
          onFinish={handleSubmit}
          {...formValidateTriggers}
          layout="vertical"
        >
          {files && files.length > 0 ? (
            <GenericSortableTable<File>
              data={files}
              setData={setTransactionFiles}
              columns={columns(
                files,
                setTransactionFiles,
                removeTransactionFile,
              )}
              className="table-no-header table-light-grey mb-16"
            />
          ) : (
            <div className="mb-16" />
          )}
          <ImportFilesCard
            description={description}
            descriptionSeparator={descriptionSeparator}
            descriptionUrlTitle={descriptionUrlTitle}
            url="#"
            accept="application/pdf"
            maxSize={MAX_SIZE_PER_FILE}
            maxSizeOfAllFiles={isSignature ? MAX_SIZE_ALL_FILES : undefined}
            extensions={["pdf"]}
            name="dropZoneCardItem"
            openFilesSelectionOnClick
            setFiles={(newFiles: File[]) =>
              setTransactionFiles(
                FileHelper.removeNameDoublon([...files, ...newFiles]),
              )
            }
            currentDocumentsSize={ArrayHelper.sumElements(
              files.map((file) => file.size ?? 0),
            )}
            currentFileCount={currentFileCount}
            maxCount={isSignature ? SIGNATURE_MAX_FILES : SCELLEMENT_MAX_FILES}
          />
          <Col span={24} xl={16}>
            <Checkbox
              checked={transactionForm.confidential}
              onChange={(e) => {
                if (!isFromModel) {
                  if (!e.target.checked) {
                    setIsConfirmModalOpen(true);
                  } else {
                    setCurrentTransactionField([
                      {
                        field: "confidential",
                        value: e.target.checked,
                      },
                    ]);
                  }
                } else {
                  setCurrentTransactionField([
                    {
                      field: "confidential",
                      value: e.target.checked,
                    },
                  ]);
                }
              }}
              className="mt-4 font-generic"
              data-test="input-label"
              data-alt={t("signature.accordion.confidential.checkBoxMessage")}
              data-test-for="confidentiality"
            >
              {t("signature.accordion.confidential.checkBoxMessage")}
              <InfoModal value="hideConfidential" info />
            </Checkbox>
          </Col>
          {!isFromModel && (
            <Confidentiality
              confidential={
                transactionForm.confidential
                  ? transactionForm.confidential
                  : false
              }
              authorizedExpertForConfidentiality={
                transactionForm.authorizedExpertForConfidentiality || []
              }
              isAuthorized
              clearSelectedExpertForConfidentiality={(email: string) => {
                setCurrentTransactionField([
                  {
                    field: "authorizedExpertForConfidentiality",
                    value:
                      transactionForm.authorizedExpertForConfidentiality?.filter(
                        (authorizedExpert) => authorizedExpert.email !== email,
                      ),
                  },
                ]);
              }}
              addSelectedExpertForConfidentiality={(
                selectedExperts: Contact[],
              ) => {
                setCurrentTransactionField([
                  {
                    field: "authorizedExpertForConfidentiality",
                    value:
                      transactionForm.authorizedExpertForConfidentiality &&
                      transactionForm.authorizedExpertForConfidentiality
                        .length > 0
                        ? [
                            ...transactionForm.authorizedExpertForConfidentiality,
                            ...selectedExperts,
                          ]
                        : [...selectedExperts],
                  },
                ]);
              }}
              officeMembers={members}
              user={user}
              officeId={transactionForm.office ?? ""}
            />
          )}
          {isFromModel &&
            setSignaturePositioning &&
            files &&
            files.length > 0 && (
              <div
                className="mt-16"
                data-test={"input-label"}
                data-alt={t(
                  `offices.transactionModel.filesToSign.${
                    isSignature ? "signature" : "scellement"
                  }.positioning`,
                )}
                data-test-for={"positioning"}
              >
                <Checkbox
                  checked={signaturePositioning}
                  onChange={() =>
                    setSignaturePositioning(!signaturePositioning)
                  }
                  className="mt-4"
                  data-test={"positioning"}
                >
                  {t(
                    `offices.transactionModel.filesToSign.${
                      isSignature ? "signature" : "scellement"
                    }.positioning`,
                  )}
                  <InfoModal value="modelSignaturePositioning" info />
                </Checkbox>
              </div>
            )}
          <div className="d-flex align-items-center justify-content-end pt-4">
            <SubmitButton
              disabled={!isFromModel && files.length === 0}
              label={isFromModel ? "buttons.next" : "buttons.validate"}
              data-test={"button"}
              data-alt={t("buttons.next")}
              className="mw-160"
            />
          </div>
        </Form>
      </Col>
      {!isFromModel && (
        <ConfirmModal
          visible={isConfirmModalOpen}
          onCancel={() => setIsConfirmModalOpen(false)}
          onOk={() => {
            setIsConfirmModalOpen(false);
            setCurrentTransactionField([
              {
                field: "confidential",
                value: false,
              },
            ]);
          }}
          title={t("signature.accordion.confidential.deleteConfidentiality")}
        />
      )}
    </Row>
  );
};

export default FilesToSignSection;
